<template>
  <div class="page-wrapper">
    <div class="train-mode">
      <div class="detail-nav">
        <button class="back-btn" @click="goBack">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="back-icon"><path d="M15 18l-6-6 6-6"/></svg>
          Back to Paper List
        </button>
        <div class="nav-right">
          <span class="status-badge">
            <span class="dot-indicator"></span>
            Training Mode
          </span>
          <span class="timer">Question {{ history.length }}</span>
          <span class="score-badge">
            ✓ {{ correctCount }} / {{ answeredCount }}
          </span>
        </div>
      </div>

      <div v-if="loading" class="empty-wrap">
        <div class="empty">
          <div class="spinner big"></div>
          <div>Loading question...</div>
        </div>
      </div>

      <div v-else-if="!question" class="empty-wrap">
        <div class="empty">No questions available, please try again later</div>
        <button class="btn btn-primary" @click="fetchQuestion">Reload</button>
      </div>

      <div v-else class="detail-card">
        <div class="paper-header">
          <div class="paper-header-bg"></div>
          <div class="paper-header-content">
            <h1 class="paper-title">Random Training</h1>
            <div class="paper-meta">
              <span v-if="question.difficulty" class="meta-item">{{ question.difficulty }}</span>
              <span v-if="question.category" class="meta-item">{{ question.category }}</span>
            </div>
          </div>
        </div>

        <div class="question-item" :class="resultClass">
          <div class="q-header">
            <span class="q-index" :class="{ answered: selectedAnswer !== null }">Q</span>
            <div class="q-center">
              <div class="q-text">{{ question.title }}</div>
            </div>
          </div>

          <div class="options">
            <div
              v-for="opt in question.options"
              :key="opt.id"
              class="option-item"
              :class="getOptionClass(opt)"
              @click="!answered && selectOption(opt)"
            >
              <span class="option-key">{{ opt.option_key }}</span>
              <span class="option-text">{{ opt.option_text }}</span>
              <span v-if="answered && isCorrectOption(opt)" class="option-mark">✓</span>
              <span v-if="answered && isWrongSelected(opt)" class="option-mark wrong">✗</span>
            </div>
          </div>

          <div v-if="answered" class="result-banner" :class="{ correct: isRight, wrong: !isRight }">
            <!--<div class="result-icon"></div>-->
            <div class="result-body">
              <div class="result-title">{{ isRight ? 'Correct!' : 'Wrong' }}</div>
              <div class="result-sub">
                Correct answer: {{ correctAnswerText }}
              </div>
              <div v-if="question.knowledge" class="result-analysis">
                <span class="kl-label">Explanation</span>
                <span class="kl-text">{{ question.knowledge }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="submit-bar">
          <button
            class="btn btn-primary btn-lg"
            :disabled="!answered || loadingNext"
            @click="nextQuestion"
          >
            <span v-if="loadingNext" class="spinner"></span>
            {{ loadingNext ? 'Loading...' : 'Next →' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import request from '@/utils/request'

const router = useRouter()

const question = ref(null)
const loading = ref(false)
const loadingNext = ref(false)
const selectedAnswer = ref(null)
const answered = ref(false)
const isRight = ref(false)

const history = ref([])
const correctCount = ref(0)

const answeredCount = computed(() => history.value.filter(h => h.correct !== null).length)

const resultClass = computed(() => {
  if (!answered.value) return ''
  return isRight.value ? 'correct' : 'wrong'
})

const correctAnswerText = computed(() => {
  const correct = question.value?.answer
  return Array.isArray(correct) ? correct.join(', ') : correct
})

const isCorrectOption = (opt) => {
  const correct = question.value?.answer
  if (Array.isArray(correct)) return correct.includes(opt.option_key)
  return String(correct) === String(opt.option_key)
}

const isWrongSelected = (opt) => {
  if (isCorrectOption(opt)) return false
  if (Array.isArray(selectedAnswer.value)) return selectedAnswer.value.includes(opt.option_key)
  return String(selectedAnswer.value) === String(opt.option_key)
}

const getOptionClass = (opt) => {
  const classes = []
  if (!answered.value) {
    if (Array.isArray(selectedAnswer.value) && selectedAnswer.value.includes(opt.option_key)) {
      classes.push('selected')
    } else if (String(selectedAnswer.value) === String(opt.option_key)) {
      classes.push('selected')
    }
    return classes
  }
  if (isCorrectOption(opt)) classes.push('correct')
  if (isWrongSelected(opt)) classes.push('wrong')
  return classes
}

const fetchQuestion = async () => {
  try {
    loading.value = true
    const ids = history.value.map(h => h.id)
    const res = await request.get('/quizen/question/one', {
      params: { exclude_ids: ids.join(',') }
    })
    question.value = res.data || null
    selectedAnswer.value = null
    answered.value = false
    isRight.value = false
    if (question.value) {
      history.value.push({ id: question.value.id, correct: null })
    }
  } catch (e) {
    question.value = null
  } finally {
    loading.value = false
  }
}

const selectOption = (opt) => {
  if (answered.value) return
  const optKey = opt.option_key
  selectedAnswer.value = optKey
  const correct = question.value.answer
  isRight.value = Array.isArray(correct)
    ? correct.includes(optKey)
    : String(correct) === String(optKey)
  answered.value = true
  const last = history.value[history.value.length - 1]
  if (last && last.correct === null) {
    last.correct = isRight.value
  }
  if (isRight.value) correctCount.value++
}

const nextQuestion = async () => {
  try {
    loadingNext.value = true
    await fetchQuestion()
  } finally {
    loadingNext.value = false
  }
}

const goBack = () => {
  router.push('/quizen/paper')
}

onMounted(() => {
  fetchQuestion()
})
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background:
    radial-gradient(ellipse at top left, rgba(99, 102, 241, 0.08), transparent 50%),
    radial-gradient(ellipse at bottom right, rgba(236, 72, 153, 0.06), transparent 50%),
    linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.train-mode {
  max-width: 900px;
  margin: 0 auto;
  padding: 16px 20px 60px;
  box-sizing: border-box;
}

.empty-wrap {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
}

.empty {
  color: #6b7280;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.detail-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back-btn {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #6366f1;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 14px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
}

.back-btn:hover {
  background: #f9fafb;
  border-color: #c7d2fe;
  transform: translateX(-2px);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.12);
}

.back-icon {
  width: 18px;
  height: 18px;
}

.timer {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 13px;
  color: #6366f1;
  background: #eef2ff;
  padding: 4px 10px;
  border-radius: 99px;
  font-weight: 600;
}

.score-badge {
  font-size: 12px;
  padding: 5px 14px;
  border-radius: 99px;
  background: #d1fae5;
  color: #065f46;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.status-badge {
  font-size: 12px;
  padding: 5px 14px;
  border-radius: 99px;
  background: #fef3c7;
  color: #92400e;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.dot-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.detail-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 28px 32px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
}

.paper-header {
  position: relative;
  padding: 28px 32px;
  margin: -28px -32px 28px;
  overflow: hidden;
  border-radius: 14px 14px 0 0;
  background: linear-gradient(135deg, #10b981 0%, #06b6d4 50%, #6366f1 100%);
  box-shadow: 0 10px 40px -10px rgba(16, 185, 129, 0.4);
}

.paper-header-bg {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 35%),
    radial-gradient(circle at 60% 60%, rgba(255, 255, 255, 0.08) 0%, transparent 30%);
  pointer-events: none;
}

.paper-header-content {
  position: relative;
  text-align: center;
  padding: 0;
  color: #fff;
}

.paper-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.5px;
}

.paper-meta {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-item {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 3px 10px;
  border-radius: 99px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.question-item {
  padding: 22px 24px;
  transition: background 0.2s;
  background: #fafbfc;
  border-radius: 12px;
  border: 1px solid #eef0f4;
}

.question-item.correct {
  background: rgba(16, 185, 129, 0.06);
  border-color: #bbf7d0;
}

.question-item.wrong {
  background: rgba(239, 68, 68, 0.05);
  border-color: #fecaca;
}

.q-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 20px;
}

.q-index {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #eef2ff, #ede9fe);
  color: #6366f1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.q-index.answered {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #fff;
}

.question-item.correct .q-index {
  background: #10b981;
}

.question-item.wrong .q-index {
  background: #ef4444;
}

.q-center {
  flex: 1;
  min-width: 0;
}

.q-text {
  font-size: 17px;
  color: #0f172a;
  line-height: 1.75;
  white-space: pre-wrap;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
  color: #374151;
  background: #fff;
}

.option-item:hover:not(.correct):not(.wrong) {
  border-color: #60a5fa;
  background: #f0f9ff;
  transform: translateX(2px);
}

.option-item.selected {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #1e40af;
}

.option-item.correct {
  border-color: #10b981;
  background: #ecfdf5;
  color: #065f46;
  cursor: default;
}

.option-item.wrong {
  border-color: #ef4444;
  background: #fef2f2;
  color: #991b1b;
  cursor: default;
}

.option-key {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.option-item.selected .option-key {
  background: #3b82f6;
  color: #fff;
}

.option-item.correct .option-key {
  background: #10b981;
  color: #fff;
}

.option-item.wrong .option-key {
  background: #ef4444;
  color: #fff;
}

.option-text {
  flex: 1;
  line-height: 1.6;
}

.option-mark {
  color: #10b981;
  font-weight: 700;
  flex-shrink: 0;
  font-size: 16px;
}

.option-mark.wrong {
  color: #ef4444;
}

.result-banner {
  margin-top: 18px;
  padding: 16px 20px;
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.result-banner.correct {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
}

.result-banner.wrong {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fecaca;
}

.result-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.result-body {
  flex: 1;
  min-width: 0;
}

.result-title {
  font-size: 16px;
  font-weight: 600;
}

.result-banner.correct .result-title {
  color: #065f46;
}

.result-banner.wrong .result-title {
  color: #991b1b;
}

.result-sub {
  font-size: 13px;
  color: #475569;
  margin-top: 2px;
}

.result-analysis {
  margin-top: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.6);
  border-left: 3px solid #6366f1;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.kl-label {
  color: #6366f1;
  font-weight: 600;
  flex-shrink: 0;
}

.kl-text {
  color: #475569;
  line-height: 1.6;
}

.submit-bar {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 500;
}

.btn-primary {
  background: linear-gradient(135deg, #10b981, #06b6d4);
  color: #fff;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-lg {
  padding: 12px 40px;
  font-size: 15px;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner.big {
  width: 28px;
  height: 28px;
  border-width: 3px;
  border-color: rgba(99, 102, 241, 0.25);
  border-top-color: #6366f1;
  margin: 0 auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .train-mode {
    padding: 14px 12px 40px;
  }

  .detail-card {
    padding: 18px 16px;
  }

  .paper-header {
    padding: 22px 20px;
    margin: -18px -16px 24px;
    border-radius: 14px 14px 0 0;
  }

  .paper-title {
    font-size: 20px;
  }

  .q-text {
    font-size: 15px;
  }

  .option-item {
    padding: 11px 12px;
    font-size: 13px;
  }

  .question-item {
    padding: 18px 16px;
  }

  .detail-nav {
    gap: 8px;
  }

  .result-banner {
    padding: 14px 16px;
  }
}
</style>